import React, { useState, useRef, useCallback } from 'react';
import { Button } from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import JSZip from 'jszip';
import ErrorDialog from './ErrorDialog';
import { Messages } from '../constants/Messages';
import DOMPurify from 'dompurify';
import { MAX_FILE_SIZE } from '../constants/Constants';

interface FileUploadProps {
    onFileSelect: (files: FileList | null) => void;
}

const FileUpload: React.FC<FileUploadProps> = ({ onFileSelect }) => {
    const [selectedFile, setSelectedFile] = useState<FileList | null>(null);
    const [dialogState, setDialogState] = useState({ open: false, message: '' });
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleCloseDialog = useCallback(() => setDialogState({ open: false, message: '' }), []);

    const showErrorDialog = useCallback((message: string) => {
        setDialogState({ open: true, message });
    }, []);

    const checkDocxContent = useCallback(async (files: FileList): Promise<FileList> => {
        const validMimeTypes = ['application/vnd.openxmlformats-officedocument.wordprocessingml.document'];

        let size = 0;
        for (const file of files) {
            size += file.size;
        }
        if (size > MAX_FILE_SIZE) {
            const message = Messages.FILE_SIZE_ERROR;
            showErrorDialog(message);
            throw new Error(message);
        }

        for (const file of files) {
            if (!file.name.endsWith('.docx') || !validMimeTypes.includes(file.type)) {
                const message = Messages.UNSUPPORTED_FILE_FORMAT;
                showErrorDialog(message);
                throw new Error(message);
            }

            let content;
            try {
                const zip = new JSZip();
                content = await zip.loadAsync(file);
            } catch (error) {
                const message = Messages.FILE_UPLOAD_ERROR;
                showErrorDialog(message);
                throw new Error(message);
            }

            if (!content.files['word/document.xml']) {
                const message = Messages.FILE_NOT_FOUND_ERROR;
                showErrorDialog(message);
                throw new Error(message);
            }
        }

        return files;
    }, [showErrorDialog]);

    const handleFileSelection = useCallback(async (files: FileList | null) => {
        if (files) {
            if (files.length > 0) {
                try {
                    await checkDocxContent(files);
                    setSelectedFile(files);
                    onFileSelect(files);
                } catch {
                    setSelectedFile(null);
                    onFileSelect(null);
                }
            }else{
                setSelectedFile(null);
                onFileSelect(null);
            }
        } else {
            setSelectedFile(null);
            onFileSelect(null);
        }
    }, [checkDocxContent, onFileSelect]);

    const handleFileChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files || null;
        handleFileSelection(files);
    }, [handleFileSelection]);

    const handleDrop = useCallback((event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        const files = event.dataTransfer.files || null;
        handleFileSelection(files);
    }, [handleFileSelection]);

    const handleReset = useCallback(() => {
        setSelectedFile(null);
        onFileSelect(null);
    }, [onFileSelect]);

    const handleDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
    }, []);

    const openFileDialog = useCallback(() => {
        fileInputRef.current?.click();
    }, []);

    const getDisplayFileName = (name: string, maxLength: number) => {
        const extIndex = name.lastIndexOf('.');
        const ext = extIndex !== -1 ? name.slice(extIndex) : '';
        const baseName = extIndex !== -1 ? name.slice(0, extIndex) : name;

        if (baseName.length > maxLength - ext.length) {
            const partLength = Math.floor((maxLength - ext.length - 3) / 2);
            return `${baseName.slice(0, partLength)}...${baseName.slice(-partLength)}${ext}`;
        }

        return `${baseName}${ext}`;
    };

    return (
        <div className='step-accordion'>
            <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <h3>STEP1: 点検対象のファイルを以下の枠内にドラッグ＆ドロップして下さい。</h3>
                </AccordionSummary>
                <AccordionDetails>
                    <div className='uploader' onClick={openFileDialog} onDragOver={handleDragOver} onDrop={handleDrop}>
                        <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleFileChange}
                            style={{ display: 'none' }}
                            accept=".docx"
                            multiple
                            data-testid="file-input"
                        />
                        {selectedFile ? (
                            <div className="file-display">
                                <p className="file-name">
                                    {Array.from(selectedFile)
                                        .map(file => DOMPurify.sanitize(getDisplayFileName(file.name, 50)))
                                        .join('\n')}
                                </p>
                                <Button onClick={handleReset} variant='outlined'>ファイルを変更する</Button>
                            </div>
                        ) : (
                            <div className='dialogMassage'>
                                <p>ドラッグ&ドロップまたは、この領域をクリックしてdocxファイルを選択してください。</p>
                                <p>同時に複数のファイルが選択できます。</p>
                                <p>ファイルの合計サイズ上限は{MAX_FILE_SIZE / 1024 / 1024}MBです。</p>
                            </div>
                        )}
                    </div>
                     <div className='caution-message'>
                        ※サーバにドラッグ＆ドロップした法令のデータが送信されることはありません
                    </div>
                </AccordionDetails>
            </Accordion>
            <ErrorDialog open={dialogState.open} errorMessage={dialogState.message} onClose={handleCloseDialog} />
        </div>
    );
};

export default FileUpload;
